/**
 * Step 判定 —— 对应技术设计 §7.2。
 *
 * 一个步骤的三类条件：
 *   success  全部满足 → 本步通过
 *   fail     任一满足 → 本步判错（不是卡死，是记一次错并给知识点，§5.7）
 *   blocked  任一满足 → 本步暂时不能推进（前置没做，如未检气密性就加热）
 *
 * 判定**只读**：不改图、不发事件。改状态是 rule-engine 的事，
 * 这样「为什么这一步过了」永远可以由条件表回溯（红线 10 的可回溯要求）。
 */

import type { CompiledCondition, EvalContext } from "../logic/condition-evaluator.js";
import { compileCondition } from "../logic/condition-evaluator.js";

export interface StepDefinition {
  id: string;
  title: string;
  /** 给学生的一句话目标（§14.2「每一步只突出当前目标」） */
  goal?: string;
  /** 本步允许的操作原语，用于 exam 模式限制误触 */
  allowedActions?: string[];
  success: string[];
  fail?: string[];
  blocked?: string[];
  /** 判错时触发的知识点 */
  knowledgeOnError?: string[];
  /** 本步期望建立的关系，喂给吸附评分的「当前任务」维度（§5.3） */
  desiredRelations?: string[];
  /** 分级提示，hintLevel 越高给得越具体 */
  hints?: string[];
}

export interface CompiledStep {
  definition: StepDefinition;
  success: CompiledCondition[];
  fail: CompiledCondition[];
  blocked: CompiledCondition[];
}

export type StepVerdict = "pending" | "passed" | "failed" | "blocked";

export function compileStep(definition: StepDefinition): CompiledStep {
  const compile = (sources: string[] | undefined, label: string): CompiledCondition[] =>
    (sources ?? []).map((source) => {
      try {
        return compileCondition(source);
      } catch (error) {
        const detail = error instanceof Error ? error.message : String(error);
        throw new Error(`步骤 "${definition.id}" 的 ${label} 条件编译失败：${detail}`);
      }
    });

  return {
    definition,
    success: compile(definition.success, "success"),
    fail: compile(definition.fail, "fail"),
    blocked: compile(definition.blocked, "blocked"),
  };
}

export function compileSteps(definitions: readonly StepDefinition[]): CompiledStep[] {
  return definitions.map(compileStep);
}

/**
 * 判定顺序：blocked → fail → success。
 *
 * blocked 优先于 fail 是有意的：前置没做时，先说「你还没做 X」比先说「你做错了」
 * 更有用（§14.1「不让学生卡死」）。
 */
export function checkStep(step: CompiledStep, context: EvalContext): StepVerdict {
  if (step.blocked.some((condition) => condition(context))) return "blocked";
  if (step.fail.some((condition) => condition(context))) return "failed";
  if (step.success.length > 0 && step.success.every((condition) => condition(context))) {
    return "passed";
  }
  return "pending";
}

/** 已满足的 success 条件数 / 总数，用于进度条与「还差一步」提示。 */
export function stepProgress(step: CompiledStep, context: EvalContext): { met: number; total: number } {
  const total = step.success.length;
  const met = step.success.filter((condition) => condition(context)).length;
  return { met, total };
}
